import {
  flexRender,
  getCoreRowModel,
  Row,
  Table as ReactTable,
  useReactTable,
} from "@tanstack/react-table";
import { FormEvent, KeyboardEvent, ReactNode, useMemo } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";

import { Meta } from "@/@types/paginated";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableLoad } from "@/components/ui/table-load";
import { accessRequestDetailPath } from "@/constants/access-request";
import { UNLOCK_TAB_LABELS } from "@/constants/messages";
import {
  UNLOCK_MIN_LAST_PAGE,
  UNLOCK_OPEN_ROW_KEYS,
  UNLOCK_SEARCH_FIELD,
} from "@/constants/unlock-request";
import { AccessRequestTypeEnum } from "@/enum/access_request";
import { AccessRequestDTO } from "@/services/dtos/accessRequest.dto";
import { useGetAccessRequestQuery } from "@/services/request-access";

import { EmptyTable } from "components/Datatable/components/EmptyTable";

import { unlockColumns } from "./unlock-columns";
import UnlockPagination from "./unlock-pagination";
import { useUnlockTabParams } from "./use-unlock-tab-params";

interface UnlockTabProps {
  detailPathPrefix: string;
  headerAction?: ReactNode;
}

export default function UnlockTab({
  detailPathPrefix,
  headerAction,
}: UnlockTabProps): JSX.Element {
  const navigate: NavigateFunction = useNavigate();
  const { page, perPage, search, goToPage, changePerPage, changeSearch } =
    useUnlockTabParams();

  const { data, isFetching, isError } = useGetAccessRequestQuery({
    page,
    perPage,
    search: search || undefined,
    type: AccessRequestTypeEnum.UNLOCK,
  });

  const rows: AccessRequestDTO[] = useMemo(() => data?.data ?? [], [data]);
  const meta: Partial<Meta> = data?.meta ?? {};
  const lastPage: number = Math.max(meta.lastPage ?? UNLOCK_MIN_LAST_PAGE, UNLOCK_MIN_LAST_PAGE);

  const table: ReactTable<AccessRequestDTO> = useReactTable({
    data: rows,
    columns: unlockColumns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: lastPage,
  });

  const openRow = (row: Row<AccessRequestDTO>): void => {
    navigate(accessRequestDetailPath(detailPathPrefix, row.original.id));
  };

  const openRowFromKeyboard = (
    event: KeyboardEvent<HTMLTableRowElement>,
    row: Row<AccessRequestDTO>,
  ): void => {
    if (!UNLOCK_OPEN_ROW_KEYS.includes(event.key)) return;

    event.preventDefault();
    openRow(row);
  };

  const submitSearch = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const value: FormDataEntryValue | null = new FormData(
      event.currentTarget,
    ).get(UNLOCK_SEARCH_FIELD);
    changeSearch(String(value ?? "").trim());
  };

  const hasRows: boolean = rows.length > 0;
  const isOutOfRange: boolean = !hasRows && page > lastPage;
  const emptyText: string = isOutOfRange
    ? UNLOCK_TAB_LABELS.PAGE_OUT_OF_RANGE
    : UNLOCK_TAB_LABELS.EMPTY;

  return (
    <div className="space-y-4" data-testid="unlock-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <form className="w-full max-w-xs" onSubmit={submitSearch} role="search">
          <Input
            key={search}
            name={UNLOCK_SEARCH_FIELD}
            aria-label={UNLOCK_TAB_LABELS.SEARCH_PLACEHOLDER}
            placeholder={UNLOCK_TAB_LABELS.SEARCH_PLACEHOLDER}
            defaultValue={search}
            onBlur={event => changeSearch(event.target.value.trim())}
          />
        </form>
        {headerAction}
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isFetching ? <TableLoad colSpan={unlockColumns.length} /> : null}

            {!isFetching && isError ? (
              <TableRow>
                <TableCell
                  colSpan={unlockColumns.length}
                  role="alert"
                  className="p-4 text-center text-sm text-red-600"
                >
                  {UNLOCK_TAB_LABELS.LOAD_FAILED}
                </TableCell>
              </TableRow>
            ) : null}

            {!isFetching && !isError && hasRows
              ? table.getRowModel().rows.map(row => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer focus-visible:bg-muted focus-visible:outline-none"
                    role="link"
                    tabIndex={0}
                    aria-label={UNLOCK_TAB_LABELS.VIEW_ROW(row.original.name)}
                    onClick={() => openRow(row)}
                    onKeyDown={event => openRowFromKeyboard(event, row)}
                  >
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : null}

            {!isFetching && !isError && !hasRows ? (
              <TableRow>
                <TableCell colSpan={unlockColumns.length} className="p-0">
                  <EmptyTable text={emptyText} />
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>

      <UnlockPagination
        lastPage={lastPage}
        rowCount={meta.total ?? rows.length}
        page={page}
        perPage={perPage}
        onPageChange={goToPage}
        onPerPageChange={changePerPage}
      />
    </div>
  );
}
